import React from 'react';
import type { DashboardExecutiveData } from '../../../types';
import HeadlineStatCard from './HeadlineStatCard';
import MetricStrip from './MetricStrip';
import TabSection from './TabSection';
import ChartCard from './charts/ChartCard';
import AreaStackChart from './charts/AreaStackChart';
import CompositionBarChart from './charts/CompositionBarChart';
import DailyFlowChart from './charts/DailyFlowChart';
import DonutChart from './charts/DonutChart';
import GaugeChart from './charts/GaugeChart';
import PlayerActivityChart from './charts/PlayerActivityChart';
import RankedBarChart from './charts/RankedBarChart';
import StackedBarChart from './charts/StackedBarChart';
import { BLUE_RAMP, CHART_COLORS, formatUsdFull } from './charts/common';
import {
  isCompositionEmpty,
  isSeriesEmpty,
  toCashSettlement,
  toExposureBars,
  toFeeSeries,
  toFlowSeries,
  toGatewayRanking,
  toMoneyInSeries,
  toOwedToPlatform,
  toPlayerSeries,
  toRevenueMix,
  toTierSplit,
} from './executiveModel';

interface OverviewTabProps {
  data: DashboardExecutiveData | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

const FEE_SERIES = [
  { dataKey: 'playerDeposits', name: 'Player deposits', color: BLUE_RAMP[1] },
  { dataKey: 'withdrawals', name: 'Withdrawals', color: CHART_COLORS.out },
  { dataKey: 'creditPurchases', name: 'Credit purchases', color: BLUE_RAMP[4] },
];

const MONEY_IN_SERIES = [
  { dataKey: 'platformPlayers', name: 'Platform players', color: BLUE_RAMP[0] },
  { dataKey: 'shopPlayers', name: 'Shop players', color: BLUE_RAMP[2] },
  { dataKey: 'network', name: 'Agent network', color: BLUE_RAMP[4] },
];

/** Credit liability, pending payouts, online debt — first two blue, the debt in the alarm hue. */
const EXPOSURE_COLORS = [BLUE_RAMP[1], BLUE_RAMP[3], CHART_COLORS.risk];

/**
 * Overview — the executive read of the platform, built to answer four questions
 * in one screen: are we making money, where does it come from, is it growing,
 * and what is at risk.
 *
 * Every KPI here is produced by the same backend function that feeds the
 * Financials tab, so the two tabs can never show different numbers for the same
 * period. All payload shaping happens in `executiveModel` — by the time data
 * reaches a chart it is already guaranteed finite and labelled.
 */
const OverviewTab: React.FC<OverviewTabProps> = ({ data, isLoading, error, onRetry }) => {
  const flow = toFlowSeries(data);
  const fees = toFeeSeries(data);
  const moneyIn = toMoneyInSeries(data);
  const revenueMix = toRevenueMix(data);
  const gateways = toGatewayRanking(data);
  const tierSplit = toTierSplit(data);
  const exposureBars = toExposureBars(data);
  const owedToPlatform = toOwedToPlatform(data);
  const cash = toCashSettlement(data);
  const players = toPlayerSeries(data?.charts);

  const hourly = data?.charts?.granularity === 'hour';
  const bucketNote = hourly ? 'Hourly buckets' : 'Daily buckets';

  return (
    <TabSection isLoading={isLoading} error={error} onRetry={onRetry}>
      {data && (
        <div className="space-y-6">
          <Section title="Headline">
            <div className="space-y-3">
              <HeadlineStatCard metric="platformNetPnl" kpi={data.kpis.platformNetPnl} />
              <MetricStrip
                columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-8"
                items={[
                  { metric: 'usdIn', kpi: data.kpis.usdIn, format: 'currency' },
                  { metric: 'payoutsOut', kpi: data.kpis.payoutsOut, format: 'currency', invertDelta: true },
                  { metric: 'netCashFlow', kpi: data.kpis.netCashFlow, format: 'currency' },
                  { metric: 'feesEarned', kpi: data.kpis.feesEarned, format: 'currency' },
                  { metric: 'exposure', kpi: data.kpis.exposure, format: 'currency' },
                  { metric: 'totalPlayers', kpi: data.kpis.totalPlayers, format: 'number' },
                  { metric: 'activePlayers', kpi: data.kpis.activePlayers, format: 'number' },
                  { metric: 'newPlayers', kpi: data.kpis.newPlayers, format: 'number' },
                ]}
              />
            </div>
          </Section>

          <Section title="Cash flow">
            <ChartCard
              metric="cashFlowTrend"
              subtitle={bucketNote}
              heightClassName="h-72"
              isEmpty={isSeriesEmpty(flow, ['usdIn', 'payoutsOut'])}
            >
              <DailyFlowChart data={flow} xKey="label" labelFormatter={(v) => v} />
            </ChartCard>
          </Section>

          <Section title="Revenue">
            <div className="grid gap-3 lg:grid-cols-2">
              <ChartCard metric="revenueMix" isEmpty={isCompositionEmpty(revenueMix)}>
                <DonutChart
                  data={revenueMix}
                  centerLabel="Net P&L"
                  centerValue={formatUsdFull(data.kpis.platformNetPnl.value)}
                />
              </ChartCard>
              <ChartCard
                metric="feesBySource"
                subtitle={bucketNote}
                isEmpty={isSeriesEmpty(fees, [
                  'playerDeposits',
                  'withdrawals',
                  'creditPurchases',
                ])}
              >
                <AreaStackChart data={fees} series={FEE_SERIES} />
              </ChartCard>
            </div>
          </Section>

          <Section title="Players">
            <div className="grid gap-3 lg:grid-cols-2">
              <ChartCard
                metric="playerActivity"
                subtitle={bucketNote}
                isEmpty={isSeriesEmpty(players, ['newPlayers', 'activePlayers'])}
                emptyMessage="No player activity in this period"
              >
                <PlayerActivityChart data={players} />
              </ChartCard>
              <ChartCard
                metric="playerGrowth"
                subtitle={bucketNote}
                isEmpty={isSeriesEmpty(players, ['newPlayers', 'totalPlayers'])}
                emptyMessage="No registered players yet"
              >
                <PlayerActivityChart data={players} variant="growth" />
              </ChartCard>
            </div>
          </Section>

          <Section title="Volume">
            <div className="grid gap-3 lg:grid-cols-3">
              <ChartCard
                metric="moneyInBySource"
                subtitle={bucketNote}
                className="lg:col-span-2"
                isEmpty={isSeriesEmpty(moneyIn, ['platformPlayers', 'shopPlayers', 'network'])}
              >
                <StackedBarChart
                  data={moneyIn}
                  series={MONEY_IN_SERIES}
                  xKey="label"
                  labelFormatter={(v) => v}
                />
              </ChartCard>
              <ChartCard metric="creditSalesByTier" isEmpty={isCompositionEmpty(tierSplit)}>
                <DonutChart data={tierSplit} />
              </ChartCard>
            </div>
          </Section>

          <Section title="Distribution & risk">
            <div className="grid gap-3 lg:grid-cols-3">
              <ChartCard
                metric="depositsByGateway"
                isEmpty={gateways.length === 0}
                emptyMessage="No gateway deposits in this period"
              >
                <RankedBarChart data={gateways} />
              </ChartCard>

              <ChartCard
                metric="exposureBreakdown"
                isEmpty={isCompositionEmpty(exposureBars)}
                emptyMessage="No outstanding exposure"
              >
                <CompositionBarChart
                  data={exposureBars}
                  totalLabel="Total exposure"
                  colors={EXPOSURE_COLORS}
                  footer={
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">
                        Shop funding debt owed to the platform
                      </span>
                      <span className="tabular-nums font-medium text-blue-950">
                        {formatUsdFull(owedToPlatform)}
                      </span>
                    </span>
                  }
                />
              </ChartCard>

              <ChartCard
                metric="cashSettlementHealth"
                isEmpty={cash.expected === 0 && cash.received === 0}
                emptyMessage="No cash settlements in this period"
              >
                <GaugeChart
                  percent={cash.collectedPct}
                  primaryLabel="Cash received"
                  primaryValue={cash.received}
                  secondaryLabel="Still uncollected"
                  secondaryValue={cash.uncollected}
                />
              </ChartCard>
            </div>
          </Section>
        </div>
      )}
    </TabSection>
  );
};

export default OverviewTab;
